import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Scan, Package, MapPin, CheckCircle2, AlertTriangle, RotateCcw, Boxes,
  Box, Zap, ListChecks, Send, Trash2, X, PartyPopper,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const FOBDirectFGPutAway = () => {
  const navigate = useNavigate();
  const bundleRef = useRef(null);
  const binRef = useRef(null);
  const usnRef = useRef(null);

  // staff | scan_bundle | scan_bin | placing | done
  const [mode, setMode] = useState('staff');
  const [bundleScan, setBundleScan] = useState('');
  const [binScan, setBinScan] = useState('');
  const [usnScan, setUsnScan] = useState('');

  // Multi-staff team (2026-07): scanned once at session start, rotated
  // round-robin across placed pieces.
  const [staffList, setStaffList] = useState([]); // [{staff_id, staff_name}]
  const [staffScanValue, setStaffScanValue] = useState('');
  const [isValidatingStaff, setIsValidatingStaff] = useState(false);
  const staffRef = useRef(null);

  const [bundle, setBundle] = useState(null);          // { bundle_code, sku_code, design_name, shade, size, usns[], usn_count, suggested_bin, suggestion_reason }
  const [eligibleSet, setEligibleSet] = useState(new Set()); // remaining (not-yet-placed) USNs of the bundle
  const [initialTotal, setInitialTotal] = useState(0);
  const [placedTotal, setPlacedTotal] = useState(0);
  const [activeBin, setActiveBin] = useState(null);
  const [suggestedBin, setSuggestedBin] = useState(null);
  const [suggestionReason, setSuggestionReason] = useState('');
  const [lastAction, setLastAction] = useState(null);

  const [isScanning, setIsScanning] = useState(false);
  const [isPlacing, setIsPlacing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fast Batch mode (SKU-locked to the scanned bundle)
  const [batchMode, setBatchMode] = useState(() => {
    try { return localStorage.getItem('fob_direct_batch_mode') === 'on'; } catch { return false; }
  });
  const [batchQueue, setBatchQueue] = useState([]);        // queued USN strings
  const [batchRejected, setBatchRejected] = useState([]);  // [{usn, reason}]

  useEffect(() => {
    try { localStorage.setItem('fob_direct_batch_mode', batchMode ? 'on' : 'off'); } catch { /* noop */ }
  }, [batchMode]);

  const focus = useCallback((ref) => setTimeout(() => ref.current?.focus(), 80), []);

  useEffect(() => {
    if (mode === 'staff') focus(staffRef);
    else if (mode === 'scan_bundle') focus(bundleRef);
    else if (mode === 'scan_bin') focus(binRef);
    else if (mode === 'placing') focus(usnRef);
  }, [mode, focus]);

  const remaining = eligibleSet.size;

  // === STAFF (multi-staff team) ===
  const handleAddStaff = async (e) => {
    e?.preventDefault?.();
    const id = staffScanValue.trim();
    if (!id || isValidatingStaff) return;
    if (staffList.some((s) => s.staff_id === id)) {
      toast.info('Staff already added');
      setStaffScanValue('');
      focus(staffRef);
      return;
    }
    setIsValidatingStaff(true);
    try {
      const { data } = await apiClient.get(`/validate/staff/${id}`);
      if (data.valid) {
        setStaffList((prev) => [...prev, { staff_id: id, staff_name: data.staff_name }]);
        toast.success(`Added ${data.staff_name}`);
      } else {
        toast.error('Invalid Staff ID');
      }
      setStaffScanValue('');
      focus(staffRef);
    } catch (err) {
      toast.error('Staff validation failed');
      setStaffScanValue('');
      focus(staffRef);
    } finally {
      setIsValidatingStaff(false);
    }
  };

  const handleRemoveStaff = (id) => setStaffList((prev) => prev.filter((s) => s.staff_id !== id));

  const handleStartSession = () => {
    if (staffList.length === 0) { toast.error('Add at least one staff'); return; }
    setMode('scan_bundle');
  };

  const staffIds = staffList.map((s) => s.staff_id);

  const resetForNext = () => {
    setBundle(null);
    setEligibleSet(new Set());
    setInitialTotal(0);
    setPlacedTotal(0);
    setActiveBin(null);
    setSuggestedBin(null);
    setSuggestionReason('');
    setBundleScan('');
    setBinScan('');
    setUsnScan('');
    setBatchQueue([]);
    setBatchRejected([]);
    setLastAction(null);
    setMode('scan_bundle');
  };

  // === STEP 1: scan bundle ===
  const handleScanBundle = async (e) => {
    e?.preventDefault?.();
    const code = bundleScan.trim();
    if (!code || isScanning) return;
    setIsScanning(true);
    try {
      const { data } = await apiClient.post('/wms/fg/put-away/fob-direct/scan', { bundle_code: code });
      setBundle(data);
      setEligibleSet(new Set(data.usns || []));
      setInitialTotal(data.usn_count || 0);
      setPlacedTotal(0);
      setSuggestedBin(data.suggested_bin || null);
      setSuggestionReason(data.suggestion_reason || '');
      setBinScan(data.suggested_bin || '');
      setBatchQueue([]);
      setBatchRejected([]);
      setMode('scan_bin');
      if ((data.already_placed_skipped || []).length > 0) {
        toast.warning(`${data.already_placed_skipped.length} piece(s) already placed — skipped`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Scan failed');
      setBundleScan('');
      focus(bundleRef);
    } finally {
      setIsScanning(false);
    }
  };

  // === STEP 2: confirm / scan bin (becomes active bin) ===
  const handleConfirmBin = async (e) => {
    e?.preventDefault?.();
    const bin = binScan.trim();
    if (!bin || isPlacing || !bundle) return;
    setIsPlacing(true);
    try {
      const { data } = await apiClient.get('/wms/fg/put-away/fob-direct/validate-bin', {
        params: { bin_code: bin, expected_sku: bundle.sku_code || '' },
      });
      if (!data.valid) {
        toast.error(data.reason || `Bin ${bin} cannot be used.`);
        focus(binRef);
        return;
      }
      setActiveBin(bin);
      setBinScan('');
      setMode('placing');
      toast.success(`Bin ${bin} active — scan pieces to place`);
      focus(usnRef);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not validate bin.');
      focus(binRef);
    } finally {
      setIsPlacing(false);
    }
  };

  // === STEP 3: scan each piece into the active bin ===
  const handlePieceScan = async (e) => {
    e?.preventDefault?.();
    const usn = usnScan.trim();
    if (!usn || isPlacing) return;
    setUsnScan('');

    // Client-side bundle-scope guard
    if (!eligibleSet.has(usn)) {
      if (batchQueue.includes(usn)) { toast.warning(`USN ${usn} already in queue`); focus(usnRef); return; }
      toast.error(`USN ${usn} is not an unplaced piece of ${bundle.bundle_code}.`);
      focus(usnRef);
      return;
    }

    if (batchMode) {
      if (batchQueue.includes(usn)) { toast.warning(`USN ${usn} already queued`); focus(usnRef); return; }
      setBatchQueue((prev) => [...prev, usn]);
      setLastAction({ usn, bin: activeBin });
      focus(usnRef);
      return;
    }

    // One-at-a-time: place immediately
    setIsPlacing(true);
    try {
      const { data } = await apiClient.post('/wms/fg/put-away/fob-direct/place-usn', {
        bundle_code: bundle.bundle_code,
        usn_code: usn,
        bin_code: activeBin,
        staff_ids: staffIds,
        staff_index: placedTotal,
      });
      setEligibleSet((prev) => { const n = new Set(prev); n.delete(usn); return n; });
      setPlacedTotal((p) => p + 1);
      setLastAction({ usn, bin: activeBin });
      if (data.bundle_completed) {
        setMode('done');
        if (data.slip_ref) toast.success(`Bundle complete! (${data.slip_ref})`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Placement failed');
    } finally {
      setIsPlacing(false);
      focus(usnRef);
    }
  };

  // === FAST BATCH submit ===
  const handleBatchSubmit = async () => {
    if (!batchQueue.length || isSubmitting || !activeBin) return;
    setIsSubmitting(true);
    try {
      const { data } = await apiClient.post('/wms/fg/put-away/fob-direct/place-batch', {
        bundle_code: bundle.bundle_code,
        bin_code: activeBin,
        usns: batchQueue,
        staff_ids: staffIds,
        staff_index: placedTotal,
      });
      const placed = data.placed || [];
      const rejected = data.rejected || [];
      if (placed.length) {
        setEligibleSet((prev) => { const n = new Set(prev); placed.forEach((u) => n.delete(u)); return n; });
        setPlacedTotal((p) => p + placed.length);
        toast.success(`Placed ${placed.length} piece(s) in ${activeBin}`);
        setLastAction({ usn: `${placed.length} pcs`, bin: activeBin });
      }
      setBatchQueue(rejected.map((r) => r.usn));
      setBatchRejected(rejected);
      if (rejected.length) toast.error(`${rejected.length} piece(s) rejected — review below`);
      if (data.bundle_completed) {
        setMode('done');
        if (data.slip_ref) toast.success(`Bundle complete! (${data.slip_ref})`);
      }
      focus(usnRef);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Batch submit failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBatchDeleteRejected = (usn) => {
    setBatchQueue((prev) => prev.filter((u) => u !== usn));
    setBatchRejected((prev) => prev.filter((r) => r.usn !== usn));
    focus(usnRef);
  };

  // === BIN FULL ===
  const handleBinFull = async () => {
    if (!activeBin || isPlacing) return;
    if (batchQueue.length) { toast.warning('Submit or clear the batch queue first.'); return; }
    setIsPlacing(true);
    try {
      const { data } = await apiClient.post('/wms/fg/put-away/fob-direct/bin-full', {
        bin_code: activeBin,
        barcode: bundle.sku_code || '',
        design_name: bundle.design_name || '',
        shade: bundle.shade || '',
        size: bundle.size || '',
      });
      toast.success(`${activeBin} marked full (${data.items_in_bin} pcs). Capacity saved.`);
      setSuggestedBin(data.new_suggested_bin || null);
      setSuggestionReason(data.new_bin_info?.suggestion_reason || '');
      setBinScan(data.new_suggested_bin || '');
      setActiveBin(null);
      setMode('scan_bin');
      if (!data.new_suggested_bin) toast.info('No suggested bin — scan any valid bin to continue.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark bin full');
    } finally {
      setIsPlacing(false);
    }
  };

  const handleChangeBin = () => {
    if (batchQueue.length) { toast.warning('Submit or clear the batch queue first.'); return; }
    setSuggestedBin(null);
    setSuggestionReason('');
    setBinScan('');
    setActiveBin(null);
    setMode('scan_bin');
  };

  // ============================ RENDER ============================
  return (
    <div className="min-h-screen bg-slate-50" data-testid="fob-direct-putaway-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900 to-slate-900 text-white px-4 py-4 shadow-lg">
        <div className="flex items-center gap-3 max-w-2xl mx-auto">
          <Button variant="ghost" size="icon" onClick={() => navigate('/wms/fg')} className="text-white hover:bg-white/10" data-testid="fob-direct-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold flex items-center gap-2"><Boxes className="w-5 h-5" /> FOB Direct FG Put-Away</h1>
            <p className="text-xs text-indigo-200 truncate">Scan a FOB bundle, then place each piece into a bin</p>
          </div>
          {/* Fast/Slow toggle */}
          <button
            type="button"
            onClick={() => {
              if (batchQueue.length) { toast.warning('Submit or clear the batch queue before changing mode.'); return; }
              setBatchMode((p) => !p);
              toast.info(`Fast Batch ${!batchMode ? 'ON' : 'OFF'}`, { duration: 1200 });
            }}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold flex items-center gap-1 transition ${batchMode ? 'bg-emerald-500 text-white' : 'bg-white/10 text-indigo-100 hover:bg-white/20'}`}
            data-testid="fob-direct-batch-toggle"
            title={batchMode ? 'Fast Batch ON — queue then submit' : 'One-at-a-time — tap for Fast Batch'}
          >
            <Zap className="w-3 h-3" /> {batchMode ? 'FAST' : 'SLOW'}
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">

        {/* STEP 0 — scan staff (multi-staff) */}
        {mode === 'staff' && (
          <Card className="border-2 border-indigo-100" data-testid="fob-direct-staff-card">
            <CardContent className="p-6">
              <div className="text-center mb-4">
                <div className="inline-flex p-3 rounded-full bg-indigo-100 mb-2"><Scan className="w-7 h-7 text-indigo-700" /></div>
                <h2 className="text-base font-semibold text-slate-800">Scan Staff ID(s)</h2>
                <p className="text-sm text-slate-500">Add one or more staff sharing this put-away. Pieces split round-robin.</p>
              </div>
              <form onSubmit={handleAddStaff} className="flex gap-2">
                <Input ref={staffRef} value={staffScanValue} onChange={(e) => setStaffScanValue(e.target.value)} placeholder="Scan / type staff ID…" className="text-base h-12" data-testid="fob-direct-staff-input" autoFocus disabled={isValidatingStaff} />
                <Button type="submit" disabled={isValidatingStaff || !staffScanValue.trim()} className="h-12 bg-indigo-700 hover:bg-indigo-800" data-testid="fob-direct-staff-add-btn">
                  {isValidatingStaff ? '…' : '+ Add'}
                </Button>
              </form>
              {staffList.length > 0 && (
                <div className="mt-4 space-y-3" data-testid="fob-direct-staff-list">
                  <p className="text-xs text-slate-500 font-medium">{staffList.length} staff added:</p>
                  <div className="flex flex-wrap gap-2">
                    {staffList.map((s) => (
                      <div key={s.staff_id} className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-full pl-3 pr-1 py-1" data-testid={`fob-direct-staff-chip-${s.staff_id}`}>
                        <span className="text-xs font-medium text-indigo-800">{s.staff_name}</span>
                        <span className="text-[10px] text-indigo-500">({s.staff_id})</span>
                        <button type="button" onClick={() => handleRemoveStaff(s.staff_id)} className="w-5 h-5 rounded-full bg-indigo-100 hover:bg-red-200 flex items-center justify-center text-indigo-700 hover:text-red-700 text-xs" data-testid={`fob-direct-staff-remove-${s.staff_id}`}>×</button>
                      </div>
                    ))}
                  </div>
                  <Button onClick={handleStartSession} disabled={staffList.length === 0} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="fob-direct-start-session-btn">
                    Start Session ({staffList.length})
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* STEP 1 — scan bundle */}
        {mode === 'scan_bundle' && (
          <Card className="border-2 border-indigo-100">
            <CardContent className="p-6">
              <div className="text-center mb-4">
                <div className="inline-flex p-3 rounded-full bg-indigo-100 mb-2"><Scan className="w-7 h-7 text-indigo-700" /></div>
                <h2 className="text-base font-semibold text-slate-800">Scan Bundle Barcode</h2>
                <p className="text-sm text-slate-500">Any FOB packed bundle received in Dispatch. Pieces are placed one-by-one.</p>
              </div>
              <form onSubmit={handleScanBundle} className="flex gap-2">
                <Input ref={bundleRef} value={bundleScan} onChange={(e) => setBundleScan(e.target.value)} placeholder="Scan / type bundle barcode…" className="text-base h-12" data-testid="fob-direct-bundle-input" autoFocus />
                <Button type="submit" disabled={isScanning || !bundleScan.trim()} className="h-12 bg-indigo-700 hover:bg-indigo-800" data-testid="fob-direct-scan-btn">
                  {isScanning ? 'Checking…' : 'Scan'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Bundle summary (visible in scan_bin + placing) */}
        {bundle && (mode === 'scan_bin' || mode === 'placing') && (
          <Card className="border-2 border-indigo-200" data-testid="fob-direct-bundle-card">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-slate-800 font-semibold"><Package className="w-5 h-5 text-indigo-700" /> {bundle.bundle_code}</div>
                <div className="flex items-center gap-1.5">
                  <Badge className="bg-emerald-100 text-emerald-800" data-testid="fob-direct-progress">{placedTotal}/{initialTotal} placed</Badge>
                  <Badge className="bg-amber-100 text-amber-800" data-testid="fob-direct-remaining">{remaining} left</Badge>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-sm">
                <div><span className="text-slate-400">Design:</span> <span className="font-medium">{bundle.design_name || '-'}</span></div>
                <div><span className="text-slate-400">SKU:</span> <span className="font-mono text-xs">{bundle.sku_code || '-'}</span></div>
                <div><span className="text-slate-400">Shade:</span> <span className="font-medium">{bundle.shade || '-'}</span></div>
                <div><span className="text-slate-400">Size:</span> <span className="font-medium">{bundle.size || '-'}</span></div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP 2 — scan / confirm bin */}
        {mode === 'scan_bin' && bundle && (
          <div className="space-y-3" data-testid="fob-direct-bin-scan-section">
            {suggestedBin ? (
              <Card className="border-2 border-blue-300 bg-blue-50">
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-blue-600 uppercase tracking-wide font-semibold mb-1">Suggested Bin</p>
                  <p className="text-xl font-black text-slate-900 tracking-wider" data-testid="fob-direct-suggested-bin">{suggestedBin}</p>
                  {suggestionReason && <p className="text-xs text-blue-500 mt-1">{suggestionReason}</p>}
                </CardContent>
              </Card>
            ) : (
              <Card className="border-2 border-amber-300 bg-amber-50">
                <CardContent className="p-3 text-center text-sm text-amber-700">No suggestion — scan/type any valid bin.</CardContent>
              </Card>
            )}
            <form onSubmit={handleConfirmBin}>
              <Card className="border-2 border-amber-300 bg-white">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2"><MapPin className="w-5 h-5 text-amber-700" /> <p className="text-sm font-semibold text-amber-800">Scan Bin to Activate</p></div>
                  <div className="flex gap-2">
                    <Input ref={binRef} value={binScan} onChange={(e) => setBinScan(e.target.value)} placeholder="Scan / type bin code…" className="text-base h-12 font-mono" data-testid="fob-direct-bin-input" autoFocus />
                    <Button type="submit" disabled={isPlacing || !binScan.trim()} className="h-12 bg-amber-600 hover:bg-amber-700" data-testid="fob-direct-bin-confirm-btn">
                      {isPlacing ? '…' : 'Activate'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </form>
            <Button variant="ghost" size="sm" onClick={resetForNext} className="text-slate-500" data-testid="fob-direct-cancel-btn">
              <RotateCcw className="w-4 h-4 mr-1" /> Cancel / scan another bundle
            </Button>
          </div>
        )}

        {/* STEP 3 — placing pieces */}
        {mode === 'placing' && bundle && activeBin && (
          <>
            {/* Active bin banner */}
            <Card className="border-2 border-emerald-400 bg-emerald-50" data-testid="fob-direct-active-bin">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Box className="w-4 h-4 text-emerald-600" /><span className="text-xs text-emerald-700 uppercase tracking-wide font-semibold">Active Bin</span></div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={handleBinFull} disabled={isPlacing} className="text-xs text-amber-700 hover:bg-amber-100 h-7 px-2" data-testid="fob-direct-bin-full-btn">
                      <AlertTriangle className="w-3 h-3 mr-1" /> Bin Full
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleChangeBin} className="text-xs text-emerald-700 hover:bg-emerald-100 h-7 px-2" data-testid="fob-direct-change-bin-btn">
                      <RotateCcw className="w-3 h-3 mr-1" /> Change
                    </Button>
                  </div>
                </div>
                <p className="text-2xl font-black text-slate-900 tracking-wider mt-1" data-testid="fob-direct-active-bin-code">{activeBin}</p>
                <p className="text-xs text-emerald-800 mt-0.5">{bundle.design_name}{bundle.shade ? ` / ${bundle.shade}` : ''}</p>
              </CardContent>
            </Card>

            {/* Piece scan input */}
            <form onSubmit={handlePieceScan}>
              <Card className="border-2 border-dashed border-slate-300">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Scan className="w-5 h-5 text-slate-800" />
                    <p className="text-sm font-semibold text-slate-800">{batchMode ? 'Scan piece — instant queue' : 'Scan each piece (USN)'}</p>
                    {batchMode && <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full"><Zap className="w-3 h-3" /> Fast</span>}
                  </div>
                  <Input ref={usnRef} value={usnScan} onChange={(e) => setUsnScan(e.target.value)} placeholder="Scan or type USN…" className="text-lg h-12 font-mono" data-testid="fob-direct-usn-input" autoFocus disabled={isPlacing || isSubmitting} />
                  {isPlacing && <p className="text-xs text-slate-500 mt-2 animate-pulse">Placing…</p>}
                </CardContent>
              </Card>
            </form>

            {/* Batch queue panel */}
            {batchMode && (batchQueue.length > 0 || batchRejected.length > 0) && (
              <Card className="border-2 border-emerald-400 bg-white" data-testid="fob-direct-batch-panel">
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center gap-2"><ListChecks className="w-4 h-4 text-emerald-600" /><span className="text-xs font-bold text-emerald-700 uppercase">Queue ({batchQueue.length})</span></div>
                  <div className="max-h-44 overflow-y-auto space-y-1 border border-slate-200 rounded p-2 bg-slate-50" data-testid="fob-direct-batch-queue">
                    {batchQueue.map((u) => {
                      const rej = batchRejected.find((r) => r.usn === u);
                      return (
                        <div key={u} className={`flex items-center justify-between gap-2 px-2 py-1 rounded text-xs font-mono ${rej ? 'bg-rose-50 border border-rose-200' : 'bg-emerald-50 border border-emerald-100'}`} data-testid={`fob-direct-queue-row-${u}`}>
                          <div className="flex-1 min-w-0">
                            <span className="flex items-center gap-1.5">
                              {rej ? <X className="w-3 h-3 text-rose-600" /> : <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
                              <span className={`truncate ${rej ? 'text-rose-800 line-through' : 'text-emerald-900'}`}>{u}</span>
                            </span>
                            {rej && <p className="text-[10px] text-rose-700 ml-4 font-sans">⚠ {rej.reason}</p>}
                          </div>
                          {rej && (
                            <button type="button" onClick={() => handleBatchDeleteRejected(u)} className="text-rose-600 hover:bg-rose-100 rounded p-1" data-testid={`fob-direct-queue-delete-${u}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <Button type="button" onClick={handleBatchSubmit} disabled={!batchQueue.length || isSubmitting || batchRejected.length === batchQueue.length} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="fob-direct-batch-submit-btn">
                    {isSubmitting ? <span className="animate-pulse">Submitting…</span> : <><Send className="w-4 h-4 mr-2" /> Submit ({batchQueue.length - batchRejected.length})</>}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Last action */}
            {lastAction && (
              <Card className="border-emerald-200 bg-emerald-50/50" data-testid="fob-direct-last-action">
                <CardContent className="p-2.5 flex items-center gap-2 text-xs text-emerald-800">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="font-mono font-medium">{lastAction.usn}</span><span>placed in</span><span className="font-bold font-mono">{lastAction.bin}</span>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* STEP 4 — done */}
        {mode === 'done' && bundle && (
          <Card className="border-2 border-green-200" data-testid="fob-direct-success-card">
            <CardContent className="p-6 text-center space-y-3">
              <div className="inline-flex p-3 rounded-full bg-green-100"><PartyPopper className="w-8 h-8 text-green-700" /></div>
              <h2 className="text-lg font-bold text-slate-800">Bundle Put Away Complete</h2>
              <p className="text-sm text-slate-600">All <b>{placedTotal}</b> piece(s) of <b>{bundle.bundle_code}</b> are stored in FG.</p>
              <Button onClick={resetForNext} className="h-12 w-full bg-indigo-700 hover:bg-indigo-800" data-testid="fob-direct-next-btn">
                <Scan className="w-4 h-4 mr-2" /> Scan Next Bundle
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default FOBDirectFGPutAway;
